import { ExchangeCode } from "@/lib/market-data/market-data.types";
import {
  CandleFetchParams,
  CandleInterval,
  CandlePoint,
  CandleUpdateCallback,
  CandleErrorCallback,
} from "./candle.types";
import { BROWSER_DIRECT_CANDLE_REST_ADAPTERS } from "./adapters/rest";
import { CANDLE_WS_ADAPTERS } from "./adapters/ws";
import { createApiCandlesRestAdapter } from "./adapters/rest/api-candles.rest";
import { requiresApiCandleFallback } from "./exchange-candle-routing";

export interface LiveCandleService {
  fetchCandles(
    exchange: ExchangeCode,
    params: CandleFetchParams
  ): Promise<CandlePoint[]>;
  subscribeCandles(
    exchange: ExchangeCode,
    symbol: string,
    interval: CandleInterval,
    onUpdate: CandleUpdateCallback,
    onError?: CandleErrorCallback
  ): () => void;
}

class DefaultLiveCandleService implements LiveCandleService {
  async fetchCandles(
    exchange: ExchangeCode,
    params: CandleFetchParams
  ): Promise<CandlePoint[]> {
    if (requiresApiCandleFallback(exchange)) {
      if (!params.userId) {
        throw new Error(`${exchange}: userId is required for API candle fetch`);
      }
      return createApiCandlesRestAdapter(params.userId).fetchCandles(params);
    }

    const adapter = BROWSER_DIRECT_CANDLE_REST_ADAPTERS[exchange];
    if (!adapter) {
      throw new Error(`${exchange}: no browser candle REST adapter`);
    }
    return adapter.fetchCandles(params);
  }

  subscribeCandles(
    exchange: ExchangeCode,
    symbol: string,
    interval: CandleInterval,
    onUpdate: CandleUpdateCallback,
    onError?: CandleErrorCallback
  ): () => void {
    return CANDLE_WS_ADAPTERS[exchange].subscribe(
      symbol,
      interval,
      onUpdate,
      onError
    );
  }
}

let singleton: LiveCandleService | null = null;

export function getLiveCandleService(): LiveCandleService {
  if (!singleton) {
    singleton = new DefaultLiveCandleService();
  }
  return singleton;
}
